'use client';

import {
  Accordion,
  AccordionItem,
  Chip,
  type ListboxProps,
  type ListboxSectionProps,
  type Selection,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
} from '@nextui-org/react';
import React from 'react';
import {
  Listbox,
  Tooltip,
  ListboxItem,
  ListboxSection,
} from '@nextui-org/react';
import { SidebarIcon as Icon, type SidebarIconName } from './SidebarIcon';
import { useLocation } from 'wouter';

import { cn } from 'common/src/utils/cn';
import { useTranslation } from 'react-i18next';

export enum SidebarItemType {
  Nest = 'nest',
  Group = 'group',
}

export type SidebarItem = {
  key: string;
  permissionKey?: string;
  title: string;
  icon?: SidebarIconName;
  href?: string;
  type?: SidebarItemType.Nest | SidebarItemType.Group;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  items?: SidebarItem[];
  className?: string;
  readAccess?: string[];
  writeAccess?: string[];
  new?: boolean;
  /** Optional custom badge label (e.g. "Beta"). Takes precedence over `new`. */
  badge?: string;
  /** Rolls up to a collapsed parent; null means the count is still unknown. */
  pendingCount?: number | null;
  /**
   * Optional allowlist of exporter IDs that can see this item. Used for
   * testing / gradual rollouts. If set, users whose exporter_id is NOT in
   * this list are hidden from the item (admins and superadmins bypass).
   */
  allowedExporterIds?: number[];
};

export type SidebarProps = Omit<ListboxProps<SidebarItem>, 'children'> & {
  items: SidebarItem[];
  isCompact?: boolean;
  hideEndContent?: boolean;
  iconClassName?: string;
  sectionClasses?: ListboxSectionProps['classNames'];
  classNames?: ListboxProps['classNames'];
  defaultSelectedKey: string;
  onSelect?: (key: string) => void;
};

const getListboxItemProps = (item: SidebarItem) => {
  const listboxItemProps = { ...item };

  delete listboxItemProps.allowedExporterIds;
  delete listboxItemProps.permissionKey;
  delete listboxItemProps.badge;
  delete listboxItemProps.pendingCount;
  delete listboxItemProps.href;
  delete listboxItemProps.icon;
  delete listboxItemProps.items;
  delete listboxItemProps.new;
  delete listboxItemProps.readAccess;
  delete listboxItemProps.type;
  delete listboxItemProps.writeAccess;

  return listboxItemProps;
};

function getSidebarPendingCount(item: SidebarItem): number | null | undefined {
  if (item.pendingCount !== undefined) return item.pendingCount;
  const counts =
    item.items
      ?.map(getSidebarPendingCount)
      .filter((count) => count !== undefined) || [];
  return counts.length
    ? counts.some((count) => count === null)
      ? null
      : counts.reduce<number>((sum, count) => sum + (count || 0), 0)
    : undefined;
}

const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      items,
      isCompact,
      defaultSelectedKey,
      onSelect,
      hideEndContent,
      sectionClasses: sectionClassesProp = {},
      itemClasses: itemClassesProp = {},
      iconClassName,
      classNames,
      className,
      ...props
    },
    ref
  ) => {
    const [selected, setSelected] =
      React.useState<React.Key>(defaultSelectedKey);
    const [, setLocation] = useLocation();
    const { t } = useTranslation('sidebar');
    const sectionClasses = {
      ...sectionClassesProp,
      base: cn(sectionClassesProp?.base, 'w-full', {
        'p-0 max-w-[44px]': isCompact,
      }),
      group: cn(sectionClassesProp?.group, {
        'flex flex-col gap-1': isCompact,
      }),
      heading: cn(sectionClassesProp?.heading, {
        hidden: isCompact,
      }),
    };

    const itemClasses = {
      ...itemClassesProp,
      base: cn(itemClassesProp?.base, {
        'w-11 h-11 gap-0 p-0': isCompact,
      }),
    };

    const renderItemBadge = React.useCallback(
      (item: SidebarItem) => {
        if (item.badge) {
          return (
            <span className='bg-brand-900 dark:bg-brand-500/20 text-white dark:text-brand-300 text-[10px] font-semibold tracking-wide uppercase h-5 px-1.5 rounded-full inline-flex items-center'>
              {t(item.badge)}
            </span>
          );
        }

        if (item.new) {
          return (
            <span className='bg-brand-900 dark:bg-brand-500/20 text-white dark:text-brand-300 text-[10px] font-semibold tracking-wide uppercase h-5 px-1.5 rounded-full inline-flex items-center'>
              {t('Nuevo')}
            </span>
          );
        }

        return null;
      },
      [t]
    );

    const renderPendingCount = React.useCallback(
      (item: SidebarItem) => {
        const count = getSidebarPendingCount(item);
        if (count === undefined) return null;
        return (
          <Chip
            size='sm'
            variant='flat'
            color={count ? 'primary' : 'default'}
            aria-label={
              count === null
                ? t('Cargando gestiones activas')
                : t('{{count}} casos con gestiones activas', { count })
            }
            className='h-5 min-w-5 shrink-0 px-0'
            classNames={{
              content: 'px-1.5 text-[11px] font-semibold tabular-nums',
            }}
          >
            {count === null ? '…' : count}
          </Chip>
        );
      },
      [t]
    );

    const handleItemSelect = React.useCallback(
      (item: SidebarItem) => {
        setSelected(item.key);
        onSelect?.(item.key);

        if (item.href) {
          setLocation(item.href);
        }
      },
      [onSelect, setLocation]
    );

    const renderNestItem = React.useCallback(
      (item: SidebarItem) => {
        const isNestType =
          item.items &&
          item.items?.length > 0 &&
          item?.type === SidebarItemType.Nest;

        if (isNestType) {
          // Is a nest type item , so we need to remove the href
          delete item.href;
        }

        const renderCompactSubItems = (
          subItems: SidebarItem[],
          depth = 0
        ): React.ReactNode =>
          subItems.map((subItem) => {
            if (subItem.items && subItem.items.length > 0) {
              return (
                <div key={subItem.key} className='flex flex-col gap-1'>
                  <div
                    className={cn(
                      'flex items-center gap-2 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-default-400',
                      depth > 0 && 'pl-5'
                    )}
                  >
                    {subItem.icon && (
                      <Icon
                        className='text-default-400'
                        icon={subItem.icon}
                        width={16}
                      />
                    )}
                    <span>{subItem.title}</span>
                  </div>
                  <div className='flex flex-col gap-1'>
                    {renderCompactSubItems(subItem.items, depth + 1)}
                  </div>
                </div>
              );
            }

            return (
              <Button
                key={subItem.key}
                variant='light'
                className={cn(
                  'justify-start text-default-500',
                  depth > 0 && 'pl-5'
                )}
                onClick={() => handleItemSelect(subItem)}
              >
                {subItem.icon && (
                  <Icon
                    className='mr-2 text-default-500'
                    icon={subItem.icon}
                    width={20}
                  />
                )}
                <span className='min-w-0 flex-1 text-left'>
                  {subItem.title}
                </span>
                {renderItemBadge(subItem)}
                {renderPendingCount(subItem)}
              </Button>
            );
          });

        const renderExpandedGroups = (
          subItems: SidebarItem[]
        ): React.ReactNode =>
          subItems.map((subItem) => {
            if (
              subItem.type === SidebarItemType.Group &&
              subItem.items &&
              subItem.items.length > 0
            ) {
              return (
                <div key={subItem.key} className='py-1'>
                  <div className='px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-default-400'>
                    {subItem.title}
                  </div>
                  <Listbox
                    className='mt-0'
                    classNames={{
                      list: cn('gap-1'),
                    }}
                    items={subItem.items}
                    variant='flat'
                  >
                    {subItem.items.map(renderItem)}
                  </Listbox>
                </div>
              );
            }

            return (
              <Listbox
                key={subItem.key}
                className='mt-0'
                classNames={{
                  list: cn('gap-1'),
                }}
                items={[subItem]}
                variant='flat'
              >
                {[subItem].map(renderItem)}
              </Listbox>
            );
          });

        const hasExpandedGroups = item.items?.some(
          (subItem) => subItem.type === SidebarItemType.Group
        );

        return (
          <ListboxItem
            {...getListboxItemProps(item)}
            key={item.key}
            classNames={{
              base: cn(
                {
                  'h-auto w-full min-w-0 p-0': !isCompact && isNestType,
                },
                {
                  // esto es para que el icono del nest item se vea bien en el sidebar compacto
                  'inline-block w-11 py-2.5': isCompact && isNestType,
                }
              ),
              title: 'w-full min-w-0',
            }}
            endContent={
              isCompact || isNestType || hideEndContent
                ? null
                : (item.endContent ?? null)
            }
            startContent={
              isCompact || isNestType ? null : item.icon ? (
                <Icon
                  className={cn(
                    'text-default-500 group-data-[selected=true]:text-foreground',
                    iconClassName
                  )}
                  icon={item.icon}
                  width={24}
                />
              ) : (
                (item.startContent ?? null)
              )
            }
            title={isCompact || isNestType ? null : item.title}
            onPress={() => {
              if (!isNestType) {
                handleItemSelect(item);
              }
            }}
          >
            {isCompact ? (
              <Popover placement='right'>
                <PopoverTrigger>
                  <div className='group/sidebar-count relative flex w-full items-center justify-center cursor-pointer'>
                    <Tooltip content={item.title} placement='right'>
                      {item.icon ? (
                        <Icon
                          className={cn(
                            'text-default-500 group-data-[selected=true]:text-foreground ',
                            iconClassName
                          )}
                          icon={item.icon}
                          width={24}
                        />
                      ) : (
                        (item.startContent ?? null)
                      )}
                    </Tooltip>
                    <span className='absolute -right-1 -top-2 group-aria-expanded/sidebar-count:hidden'>
                      {renderPendingCount(item)}
                    </span>
                  </div>
                </PopoverTrigger>

                <PopoverContent>
                  <div className='p-2'>
                    {item.items && item.items.length > 0 && (
                      <div className='flex flex-col gap-2'>
                        {renderCompactSubItems(item.items)}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            ) : null}
            {!isCompact && isNestType ? (
              <Accordion className='w-full min-w-0 p-0'>
                <AccordionItem
                  key={item.key}
                  aria-label={item.title}
                  classNames={{
                    base: 'group/sidebar-section',
                    heading: 'pr-3',
                    trigger:
                      'grid w-full grid-cols-[minmax(0,1fr)_1rem] gap-2 p-0',
                    titleWrapper: 'min-w-0',
                    indicator: 'flex h-4 w-4 items-center justify-center',
                    content: 'py-0 pl-4',
                  }}
                  title={
                    item.icon ? (
                      <div
                        className={
                          'flex min-h-11 w-full min-w-0 items-center justify-between gap-2 px-2 py-1.5'
                        }
                      >
                        <div className='flex min-w-0 items-center gap-2'>
                          <Icon
                            className={cn(
                              'shrink-0 text-default-500 group-data-[selected=true]:text-foreground',
                              iconClassName
                            )}
                            icon={item.icon}
                            width={24}
                          />
                          <span className='min-w-0 whitespace-normal text-small font-medium leading-tight text-default-500 group-data-[selected=true]:text-foreground'>
                            {item.title}
                          </span>
                          {item.items?.some(
                            (c) =>
                              c.new ||
                              c.badge ||
                              c.items?.some((g) => g.new || g.badge)
                          ) && (
                            <span
                              className='ml-0.5 h-2 w-2 shrink-0 rounded-full bg-brand-900 dark:bg-brand-300'
                              aria-hidden
                            />
                          )}
                        </div>
                        <span className='inline-flex shrink-0 items-center gap-1'>
                          {!hideEndContent ? renderItemBadge(item) : null}
                          <span className='group-data-[open=true]/sidebar-section:hidden'>
                            {renderPendingCount(item)}
                          </span>
                        </span>
                      </div>
                    ) : (
                      (item.startContent ?? null)
                    )
                  }
                >
                  {item.items && item.items?.length > 0 ? (
                    hasExpandedGroups ? (
                      <div className='mt-0.5 border-l border-default-200 pl-4'>
                        {renderExpandedGroups(item.items)}
                      </div>
                    ) : (
                      <Listbox
                        className={'mt-0.5'}
                        classNames={{
                          list: cn('border-l border-default-200 pl-4'),
                        }}
                        items={item.items}
                        variant='flat'
                      >
                        {item.items.map(renderItem)}
                      </Listbox>
                    )
                  ) : (
                    renderItem(item)
                  )}
                </AccordionItem>
              </Accordion>
            ) : null}
          </ListboxItem>
        );
      },
      [
        handleItemSelect,
        hideEndContent,
        iconClassName,
        isCompact,
        items,
        renderItemBadge,
        renderPendingCount,
      ]
    );

    const renderItem = React.useCallback(
      (item: SidebarItem) => {
        const isNestType =
          item.items &&
          item.items?.length > 0 &&
          item?.type === SidebarItemType.Nest;

        if (isNestType) {
          return renderNestItem(item);
        }

        return (
          <ListboxItem
            {...getListboxItemProps(item)}
            key={item.key}
            endContent={
              isCompact || hideEndContent ? null : (
                <span className='inline-flex items-center gap-1'>
                  {renderItemBadge(item)}
                  {renderPendingCount(item)}
                  {item.endContent}
                </span>
              )
            }
            startContent={
              isCompact ? null : item.icon ? (
                <Icon
                  className={cn(
                    'text-default-500 group-data-[selected=true]:text-foreground',
                    iconClassName
                  )}
                  icon={item.icon}
                  width={24}
                />
              ) : (
                (item.startContent ?? null)
              )
            }
            textValue={item.title}
            title={isCompact ? null : item.title}
            classNames={{
              title: cn(
                'whitespace-normal leading-tight text-small font-medium text-default-500 group-data-[selected=true]:text-foreground',
                itemClasses?.title
              ),
            }}
            onPress={() => handleItemSelect(item)}
          >
            {isCompact ? (
              <Tooltip content={item.title} placement='right'>
                <div className='flex w-full items-center justify-center'>
                  {item.icon ? (
                    <Icon
                      className={cn(
                        'text-default-500 group-data-[selected=true]:text-foreground',
                        iconClassName
                      )}
                      icon={item.icon}
                      width={24}
                    />
                  ) : (
                    (item.startContent ?? null)
                  )}
                </div>
              </Tooltip>
            ) : null}
          </ListboxItem>
        );
      },
      [
        isCompact,
        hideEndContent,
        iconClassName,
        handleItemSelect,
        itemClasses?.base,
        itemClasses?.title,
        renderItemBadge,
        renderPendingCount,
      ]
    );

    return (
      <Listbox
        key={isCompact ? 'compact' : 'default'}
        ref={ref}
        hideSelectedIcon
        as='nav'
        className={cn('list-none', className)}
        classNames={{
          ...classNames,
          list: cn('items-center', classNames?.list),
        }}
        color='default'
        itemClasses={{
          ...itemClasses,
          base: cn(
            'min-h-11 rounded-small h-auto py-2 no-underline hover:no-underline focus:no-underline active:no-underline data-[selected=true]:bg-default-100',
            itemClasses?.base
          ),
          title: cn(
            'text-small font-medium text-default-500 no-underline hover:no-underline group-data-[selected=true]:text-foreground',
            itemClasses?.title
          ),
        }}
        items={items}
        selectedKeys={[selected] as unknown as Selection}
        selectionMode='single'
        variant='flat'
        onSelectionChange={(keys) => {
          const key = Array.from(keys)[0];

          setSelected(key as React.Key);
          onSelect?.(key as string);
        }}
        {...props}
      >
        {(item) => {
          return item.items &&
            item.items?.length > 0 &&
            item?.type === SidebarItemType.Nest ? (
            renderNestItem(item)
          ) : item.items && item.items?.length > 0 ? (
            <ListboxSection
              key={item.key}
              classNames={sectionClasses}
              showDivider={isCompact}
              title={item.title}
            >
              {item.items.map(renderItem)}
            </ListboxSection>
          ) : (
            renderItem(item)
          );
        }}
      </Listbox>
    );
  }
);

Sidebar.displayName = 'Sidebar';

export default Sidebar;
